import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import Icon from 'react-native-vector-icons/Ionicons';

import { MyClass, MyClassesResponse } from '@/types/api';
import {
  GROUP_LABELS,
  classWindow,
  focusRotation,
  formatClockTime,
  formatDayLabel,
  formatRemaining,
  formatRowTime,
  parseHHMM,
  pickNowNext,
} from '@/utils/rotations';
import { colors, font, space, radius, shadow } from '@/theme/chalkCourt';

/**
 * Shared pieces of the Classes tab: the day picker and one person's day —
 * NOW / NEXT cards (live rotation + countdown, kiosk rules) and the list of
 * the day's classes. Used for "My Classes" and an admin's view of a staff member.
 */

export const LOCATION_NAMES: Record<string, string> = { SL: 'Southlake', RM: 'Rhome' };

export const classMeta = (cls: MyClass): string =>
  [
    `${formatClockTime(cls.start)} – ${formatClockTime(cls.end)}`,
    cls.location ? LOCATION_NAMES[cls.location] : null,
  ]
    .filter(Boolean)
    .join(' · ');

/**
 * Now / Next card: the class's featured rotation (the running one with its
 * countdown, else the next to start) and the rotation after it — the
 * at-a-glance answer to "where do I take my kids now?".
 */
export const ClassCard: React.FC<{
  cls: MyClass;
  label: 'NOW' | 'NEXT';
  nowSec: number;
  compact?: boolean;
  onPress: () => void;
}> = ({ cls, label, nowSec, compact, onPress }) => {
  const focus = focusRotation(cls, nowSec);
  const after = focus ? cls.rotations[cls.rotations.indexOf(focus.rotation) + 1] : undefined;
  const isNow = label === 'NOW';
  return (
    <TouchableOpacity
      style={[styles.card, isNow && styles.cardNow]}
      activeOpacity={0.85}
      onPress={onPress}
      testID={`class-card-${label.toLowerCase()}`}>
      <View style={styles.cardHead}>
        <Text style={[styles.cardLabel, isNow && styles.cardLabelNow]}>{label}</Text>
        {cls.subbingFor && (
          <Text style={styles.subTag}>SUBBING FOR {cls.subbingFor.toUpperCase()}</Text>
        )}
      </View>
      <Text style={styles.cardTitle}>{cls.name}</Text>
      <Text style={styles.cardMeta}>{classMeta(cls)}</Text>

      {focus ? (
        <View style={[styles.focus, focus.running ? styles.focusRunning : styles.focusUpcoming]}>
          <View style={styles.focusMain}>
            <Text style={styles.focusApparatus} numberOfLines={1}>
              {focus.rotation.apparatus}
            </Text>
            <Text style={styles.focusStation} numberOfLines={1}>
              {focus.rotation.station}
            </Text>
          </View>
          <View style={styles.focusSide}>
            <Text style={styles.focusTime}>
              {focus.running
                ? formatRemaining(parseHHMM(focus.rotation.end) * 60 - nowSec)
                : formatRowTime(focus.rotation.start)}
            </Text>
            <Text style={styles.focusTimeLabel}>{focus.running ? 'LEFT' : 'STARTS'}</Text>
          </View>
        </View>
      ) : (
        <Text style={styles.noRotations}>
          {cls.rotations.length === 0 ? 'No rotations set up for this class' : 'Rotations finished'}
        </Text>
      )}

      {!compact && after && (
        <Text style={styles.then}>
          Then {formatRowTime(after.start)} · {after.apparatus} · {after.station}
        </Text>
      )}
      <View style={styles.cardFoot}>
        <Text style={styles.cardFootText}>
          {cls.students.length} {cls.students.length === 1 ? 'student' : 'students'}
          {cls.rotations.length > 0 ? ` · ${cls.rotations.length} rotations` : ''}
        </Text>
        <Icon name="chevron-forward" size={18} color={colors.inkSoft} />
      </View>
    </TouchableOpacity>
  );
};

/** ‹ Friday, Oct 23 › with "Back to today" when another day is shown. */
export const DayPicker: React.FC<{
  date: string | null;
  isToday: boolean;
  canPrev: boolean;
  canNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
}> = ({ date, isToday, canPrev, canNext, onPrev, onNext, onToday }) => (
  <View style={styles.dayRow}>
    <TouchableOpacity
      onPress={onPrev}
      disabled={!canPrev}
      style={styles.dayArrow}
      accessibilityLabel="Previous day"
      testID="day-prev">
      <Icon name="chevron-back" size={24} color={canPrev ? colors.ink : colors.line} />
    </TouchableOpacity>
    <View style={styles.dayCenter}>
      <Text style={styles.dayLabel}>{date ? formatDayLabel(date) : ' '}</Text>
      {date && !isToday && (
        <TouchableOpacity onPress={onToday} testID="day-today">
          <Text style={styles.todayLink}>Back to today</Text>
        </TouchableOpacity>
      )}
    </View>
    <TouchableOpacity
      onPress={onNext}
      disabled={!canNext}
      style={styles.dayArrow}
      accessibilityLabel="Next day"
      testID="day-next">
      <Icon name="chevron-forward" size={24} color={canNext ? colors.ink : colors.line} />
    </TouchableOpacity>
  </View>
);

/** One person's day: NOW / NEXT (today only), then every class. */
export const ClassDay: React.FC<{
  data: MyClassesResponse;
  isToday: boolean;
  nowSec: number;
  onOpen: (cls: MyClass) => void;
  /** Shown when today's classes are all over. */
  doneText: string;
  /** Shown for a day with no classes (not Sunday). */
  emptyText: string;
}> = ({ data, isToday, nowSec, onOpen, doneText, emptyText }) => {
  const { now, next } = isToday ? pickNowNext(data.classes, nowSec) : { now: null, next: null };
  return (
    <>
      {isToday && now && (
        <ClassCard cls={now} label="NOW" nowSec={nowSec} onPress={() => onOpen(now)} />
      )}
      {isToday && next && (
        <ClassCard
          cls={next}
          label="NEXT"
          nowSec={nowSec}
          compact={!!now}
          onPress={() => onOpen(next)}
        />
      )}
      {isToday && !now && !next && data.classes.length > 0 && (
        <Text style={styles.doneText}>{doneText}</Text>
      )}

      <Text style={styles.sectionTitle}>
        {isToday ? "TODAY'S CLASSES" : 'CLASSES'} · {data.classes.length}
      </Text>
      {data.classes.length === 0 ? (
        <Text style={styles.empty}>
          {data.dayCode === null ? 'No classes on Sundays.' : emptyText}
        </Text>
      ) : (
        data.classes.map((cls) => {
          const w = classWindow(cls);
          const status = !isToday
            ? null
            : nowSec > w.end
            ? 'DONE'
            : nowSec >= w.start
            ? 'NOW'
            : null;
          return (
            <TouchableOpacity
              key={cls.key}
              style={[styles.listRow, status === 'DONE' && styles.listRowDone]}
              activeOpacity={0.8}
              onPress={() => onOpen(cls)}
              testID={`class-row-${cls.key}`}>
              <View style={styles.listTimeCol}>
                <Text style={styles.listTime}>{formatRowTime(cls.start)}</Text>
                <Text style={styles.listAmPm}>{parseHHMM(cls.start) < 720 ? 'AM' : 'PM'}</Text>
              </View>
              <View style={styles.listMain}>
                <Text style={styles.listName} numberOfLines={1}>
                  {cls.name}
                </Text>
                <Text style={styles.listMeta} numberOfLines={1}>
                  {GROUP_LABELS[cls.group]}
                  {cls.location ? ` · ${LOCATION_NAMES[cls.location]}` : ''}
                  {` · ${cls.students.length} students`}
                  {cls.rotations.length === 0 ? ' · no rotations' : ''}
                  {cls.subbingFor ? ` · sub for ${cls.subbingFor}` : ''}
                </Text>
              </View>
              {status && (
                <Text style={[styles.status, status === 'NOW' && styles.statusNow]}>{status}</Text>
              )}
              <Icon name="chevron-forward" size={18} color={colors.inkFaint} />
            </TouchableOpacity>
          );
        })
      )}
    </>
  );
};

const styles = StyleSheet.create({
  dayRow: { flexDirection: 'row', alignItems: 'center', marginBottom: space.lg },
  dayArrow: { padding: space.sm },
  dayCenter: { flex: 1, alignItems: 'center' },
  dayLabel: { fontFamily: font.displayBold, fontSize: 22, color: colors.ink },
  todayLink: { fontFamily: font.bodySemi, fontSize: 13, color: colors.accent, marginTop: 2 },

  card: {
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    padding: space.lg,
    marginBottom: space.md,
    ...shadow.card,
  },
  cardNow: { borderWidth: 2, borderColor: colors.accent, ...shadow.live },
  cardHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  cardLabel: { fontFamily: font.bodyBold, fontSize: 13, letterSpacing: 2.5, color: colors.inkSoft },
  cardLabelNow: { color: colors.accent },
  subTag: { fontFamily: font.bodyBold, fontSize: 11, letterSpacing: 1, color: colors.warn },
  cardTitle: {
    fontFamily: font.display,
    fontSize: 30,
    lineHeight: 32,
    color: colors.ink,
    marginTop: 2,
  },
  cardMeta: { fontFamily: font.bodyMed, fontSize: 14, color: colors.inkSoft, marginTop: 2 },

  focus: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: radius.md,
    padding: space.md,
    marginTop: space.md,
  },
  focusRunning: { backgroundColor: colors.courtSoft },
  focusUpcoming: { backgroundColor: colors.surfaceSunk },
  focusMain: { flex: 1 },
  focusApparatus: { fontFamily: font.display, fontSize: 34, lineHeight: 36, color: colors.ink },
  focusStation: { fontFamily: font.bodySemi, fontSize: 17, color: colors.inkSoft },
  focusSide: { alignItems: 'flex-end', marginLeft: space.md },
  focusTime: { fontFamily: font.display, fontSize: 30, lineHeight: 32, color: colors.court },
  focusTimeLabel: {
    fontFamily: font.bodyBold,
    fontSize: 11,
    letterSpacing: 1.5,
    color: colors.inkSoft,
  },
  noRotations: { fontFamily: font.body, fontSize: 14, color: colors.inkFaint, marginTop: space.md },
  then: { fontFamily: font.bodyMed, fontSize: 14, color: colors.inkSoft, marginTop: space.sm },
  cardFoot: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: space.md,
    paddingTop: space.sm,
    borderTopWidth: 1,
    borderTopColor: colors.line,
  },
  cardFootText: { fontFamily: font.bodyMed, fontSize: 13, color: colors.inkSoft },

  doneText: { fontFamily: font.bodyMed, fontSize: 15, color: colors.court, marginBottom: space.md },
  sectionTitle: {
    fontFamily: font.bodyBold,
    fontSize: 13,
    letterSpacing: 2,
    color: colors.inkSoft,
    marginTop: space.md,
    marginBottom: space.sm,
  },
  empty: { fontFamily: font.body, fontSize: 15, color: colors.inkSoft },

  listRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderRadius: radius.md,
    paddingVertical: space.md,
    paddingHorizontal: space.md,
    marginBottom: space.sm,
  },
  listRowDone: { backgroundColor: colors.surfaceSunk, opacity: 0.7 },
  listTimeCol: { width: 54 },
  listTime: { fontFamily: font.displayBold, fontSize: 20, lineHeight: 22, color: colors.ink },
  listAmPm: { fontFamily: font.bodyBold, fontSize: 10, letterSpacing: 1, color: colors.inkFaint },
  listMain: { flex: 1, marginRight: space.sm },
  listName: { fontFamily: font.bodySemi, fontSize: 16, color: colors.ink },
  listMeta: { fontFamily: font.body, fontSize: 13, color: colors.inkSoft, marginTop: 1 },
  status: {
    fontFamily: font.bodyBold,
    fontSize: 11,
    letterSpacing: 1.5,
    color: colors.inkFaint,
    marginRight: space.xs,
  },
  statusNow: { color: colors.accent },
});
